/**
 * Amberden — the spec block follows the size picker (2026-09-09).
 *
 * The Dimensions row and the tear-sheet link are per variant (Corvus: "H 34 cm"
 * against "H 42 × Ø 35 cm"), and both are rendered by Liquid in
 * blocks/amberden-specs.liquid. On a size change Horizon morphs only the
 * variant picker and lets every component that cares refresh itself from the
 * section it re-fetches — assets/product-price.js, product-inventory.js and
 * media-gallery.js all do exactly this. The spec block did not, so the column
 * showed "H 42 × Ø 35 cm" with 34 cm selected until a reload. Seen in the
 * mobile screenshot of the gallery fix, the same day.
 *
 * Same pattern as product-price.js: listen for the product select event on the
 * closest section, wait for its promise (it resolves with the re-rendered page),
 * find this block's counterpart by block id, and morph this element into it.
 * Morphing rather than replacing keeps the element, its listener and any open
 * state; the only nodes that change are the ones whose text or attributes did.
 */
import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

class AmberdenSpecs extends HTMLElement {
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /** @param {Event & { promise?: Promise<{ detail?: { html?: Document, productId?: string } }> }} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (!event.promise) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        // A pick on another product's form in this section (a quick-add, say) is not ours.
        if (detail.productId && this.dataset.productId && detail.productId !== this.dataset.productId) return;

        const next = detail.html.querySelector(`amberden-specs[data-block-id="${this.dataset.blockId}"]`);
        if (!next) return;

        morph(this, next);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[amberden-specs] product select rejected:', error);
      });
  };
}

if (!customElements.get('amberden-specs')) {
  customElements.define('amberden-specs', AmberdenSpecs);
}
